import Resource from '@/api/Resource';
import Platform from '@/models/Platform';
import TaskStatus from '@/models/TaskStatus';
import User from '@/models/User';
import DateHelper from '@/helpers/date';
import TaskFile from '@/models/TaskFile';

export default class extends Resource {
  $name = 'team-tasks';

  $defaults() {
    return {
      id: null,
      active: 1,
      status_id: 0,
      label_id: 0,
      author_id: 0,
      executor_id: 0,
      platform_id: 0,
      priority: 0,
      title: '',
      body: '',
      support_comment: '',
      begin_at: null,
      finish_at: null,
      deadline_at: null,
      format_deadline_at: null,
      human_deadline_at: null,
      is_deadline_end: null,
      updated_at: null,
      created_at: null,
      human_created_at: null,
      type: null,
      team_status: null,
      team_status_label: null,
      is_team: null,
      is_bug: null,
      team_index: null,
      team_status_at: null,
      team_wait_at: null,
      team_review_at: null,
      team_progress_at: null,
      team_done_at: null,
      done_at: null,

      files: [],
    };
  }

  $fill({ status, author, platform, files, executor, deadline_at}) {
    if (status) {
      this.status = new TaskStatus(status);
    }
    if (author) {
      this.author = new User(author);
    }
    if (executor) {
      this.executor = new User(executor);
    }
    if (platform) {
      this.platform = new Platform(platform);
    }
    if (files) {
      this.files = TaskFile.collection(files);
    }
    if (deadline_at) {
      this.deadline_at = new Date(deadline_at);
    }
  }

  get platformName() {
    return this.platform ? this.platform.name : '';
  }

  get helpdeskStatusColors() {
    return {
      new: 'has-background-danger',
      progress: 'has-background-info',
      wait: 'has-background-grey',
      success: 'has-background-success',
      cancel: 'has-background-grey',
      reopen: 'has-background-danger',
      deployment: 'has-background-info',
      future: 'has-background-grey',
      answered: 'has-background-danger',
      testing: 'has-background-warning',
    };
  }

  get teamStatuses() {
    return {
      draft: {
        name: 'Черновик',
        color: 'has-background-grey',
      },
      wait: {
        name: 'Можно брать в работу',
        color: 'has-background-danger',
      },
      progress: {
        name: 'В работе',
        color: 'has-background-info',
      },
      review: {
        name: 'На проверке',
        color: 'has-background-warning',
      },
      done: {
        name: 'Выполнено',
        color: 'has-background-success',
      },
    };
  }

  get sortedTeamStatuses() {
    return ['progress', 'wait', 'review', 'draft'];
  }

  get executors() {
    return {
      1: 'Дмитрий',
      4: 'Иван',
      93: 'Стас',
      55: 'Ирина',
    };
  }

  get executorName() {
    return this.executors[this.executor_id] || '';
  }

  get createdAt() {
    if (this.isHelpdeskTask) {
      return this.created_at;
    }
    if (this.isTeamDraft) {
      return this.created_at;
    }
    return this.team_wait_at ? this.team_wait_at : this.created_at;
  }

  get isInTasks() {
    return (this.isHelpdeskTask && this.is_team) || this.isTeamTask;
  }

  get isHelpdeskTask() {
    return this.type === 'helpdesk';
  }

  get isTeamTask() {
    return this.type === 'team';
  }

  get isBacklogTask() {
    return this.type === 'backlog';
  }

  get isTeamDraft() {
    return this.team_status === 'draft';
  }

  get isTeamWait() {
    return this.team_status === 'wait';
  }

  get isTeamReview() {
    return this.team_status === 'review';
  }

  get isTeamProgress() {
    return this.team_status === 'progress';
  }

  get isTeamDone() {
    return this.team_status === 'done';
  }

  get isHelpdeskDone() {
    if (!this.status) {
      return false;
    }
    return this.status.isDone;
  }

  get isTeamActive() {
    return this.team_status !== 'draft' && this.team_status !== 'done';
  }

  get statusDates() {
    if (this.isTeamTask || (this.isHelpdeskTask && this.is_team)) {
      if (this.isTeamReview) {
        if (this.isTeamTask) {
          return (this.team_progress_at ? ('В работе с ' + DateHelper.format(this.team_progress_at)) : '') +
            (this.team_review_at ? (' На проверке с ' + DateHelper.format(this.team_review_at)) : '');
        } else {
          return this.team_review_at ? (' На проверке с ' + DateHelper.format(this.team_review_at)) : '';
        }
      }
      if (this.isTeamProgress) {
        return this.team_progress_at ? ('В работе с ' + DateHelper.format(this.team_progress_at)) : '';
      }
      if (this.isTeamDone) {
        return (this.team_progress_at ? ('В работе с ' + DateHelper.format(this.team_progress_at)) : '') +
          (this.team_done_at ? ((this.isTeamTask ? ' Выполнена ' : ' Выполнен ') + DateHelper.format(this.team_done_at)) : '');
      }
    } else if (this.isHelpdeskTask) {
      if (this.status.isTypeSuccess) {
        return this.finish_at ? 'Выполнен ' + DateHelper.format(this.finish_at) : '';
      }
    }
  }

  get statusLabel() {
    if (this.isTeamTask) {
      if (this.teamStatuses[this.team_status]) {
        return this.teamStatuses[this.team_status].name;
      }
    } else if (this.isHelpdeskTask) {
      if (this.status) {
        return this.status.name;
      }
    }
    return 'Неизвестный статус';
  }

  get statusTeamLabel() {
    if (this.teamStatuses[this.team_status]) {
      return this.teamStatuses[this.team_status].name;
    }
    return 'Неизвестный статус';
  }

  get statusColor() {
    if (this.teamStatuses[this.team_status]) {
      return this.teamStatuses[this.team_status].color;
    }
    return 'has-background-grey';
  }

  get statusTextColor() {
    if (this.isTeamTask) {
      if (this.teamStatuses[this.team_status]) {
        return this.teamStatuses[this.team_status].color.replace('background', 'text');
      }
    } else if (this.isHelpdeskTask && this.status) {
      if (this.status) {
        return this.helpdeskStatusColors[this.status.type].replace('background', 'text');
      }
    }
    return 'has-background-grey';
  }

  get statusTeamTextColor() {
    if (this.teamStatuses[this.team_status]) {
      return this.teamStatuses[this.team_status].color.replace('background', 'text');
    }
    return 'has-background-grey';
  }
}
